import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Shield, Clock } from 'lucide-react';
import { leadsAPI } from '@/lib/api';
import { toast } from 'sonner';

const services = [
  'GST Returns', 'Income Tax', 'TDS Returns', 'Audit Services', 'ROC Compliance', 'Advisory Services', 'Other'
];

const BookAppointmentPage = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company_name: '',
    service_interested: '', preferred_date: '', preferred_time: '', notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await leadsAPI.create(form);
      toast.success('Appointment request submitted! We will contact you within 24 hours.');
      setForm({ name: '', email: '', phone: '', company_name: '', service_interested: '', preferred_date: '', preferred_time: '', notes: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section">
      <div className="container-grid">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">Book an Appointment</h1>
          <p className="text-muted-foreground mt-3">Schedule a free consultation with our experts</p>
        </div>

        <div className="grid md:grid-cols-12 gap-8 max-w-5xl mx-auto">
          <div className="md:col-span-7">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Request Details</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Service Interested In</Label>
                    <Select value={form.service_interested} onValueChange={v => setForm({...form, service_interested: v})}>
                      <SelectTrigger data-testid="appointment-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Preferred Date</Label>
                      <Input type="date" value={form.preferred_date} onChange={e => setForm({...form, preferred_date: e.target.value})} data-testid="appointment-date-picker" />
                    </div>
                    <div>
                      <Label>Preferred Time</Label>
                      <Select value={form.preferred_time} onValueChange={v => setForm({...form, preferred_time: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'].map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="appointment-name" />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="appointment-email" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Phone</Label>
                      <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="appointment-phone" />
                    </div>
                    <div>
                      <Label>Company Name</Label>
                      <Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="appointment-company" />
                    </div>
                  </div>

                  <div>
                    <Label>Additional Notes</Label>
                    <Textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Any specific requirements or questions..." data-testid="appointment-notes" />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="appointment-submit-button">
                    {loading ? 'Submitting...' : 'Request Appointment'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="md:col-span-5 space-y-6">
            <Card className="bg-accent/30">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold">What to Expect</h3>
                {[
                  { icon: CheckCircle, text: 'Free initial consultation (30 minutes)' },
                  { icon: Clock, text: 'We respond within 24 hours' },
                  { icon: Shield, text: 'Complete confidentiality guaranteed' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <item.icon className="h-5 w-5 text-[hsl(156,38%,32%)] flex-shrink-0" />
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <img
                  src="https://images.unsplash.com/photo-1573497161223-d9c42d7b0bad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50YW50JTIwb2ZmaWNlJTIwY29uc3VsdGF0aW9ufGVufDB8fHxibHVlfDE3NzUyMzA4NDB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Our office"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <p className="text-sm text-muted-foreground">Our team of experienced CAs is ready to help you navigate compliance with confidence.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookAppointmentPage;
